import classNames from 'classnames';
import { Trans, useTranslation } from 'next-i18next';
import type { MouseEvent } from 'react';
import { useEffect, useMemo, useRef } from 'react';

import { V1A_NAMESPACE } from '../constants';
import { loadGeoData } from '../geo/geo';
import { useHtmlLang } from '../hooks/use-html-lang';
import { reducedMotionQuery } from '../hooks/use-reduced-motion';
import { useV1aTrial } from '../trial-context';
import type { McStrings } from './engine';
import { startMulticonexao } from './engine';
import styles from './multiconexao.module.css';

/**
 * The five rows ship in their baseline state (São Paulo winning at 42 ms against the 208 ms direct line), so
 * the diagram says what it has to say before, and without, the engine; `startMulticonexao` then relocates
 * them for the visitor and keeps them live.
 */
const STATIC_ROUTES = [
  { id: 'r1', code: 'MIA', city: 'Miami', cc: 'US', ms: 95, fill: '1.5,18 1.5,1.5 8.5,5 15.5,2.3 22.5,1.5 29.5,4.1 36.5,5.9 43.5,2.3 50.5,3.2 50.5,18', line: '1.5,1.5 8.5,5 15.5,2.3 22.5,1.5 29.5,4.1 36.5,5.9 43.5,2.3 50.5,3.2', dotY: '3.2' },
  { id: 'r2', code: 'RIO', city: 'Rio de Janeiro', cc: 'BR', ms: 52, fill: '1.5,18 1.5,10.3 8.5,13 15.5,9.4 22.5,11.2 29.5,12.1 36.5,8.5 43.5,10.3 50.5,11.2 50.5,18', line: '1.5,10.3 8.5,13 15.5,9.4 22.5,11.2 29.5,12.1 36.5,8.5 43.5,10.3 50.5,11.2', dotY: '11.2' },
  { id: 'r3', code: 'SAO', city: 'São Paulo', cc: 'BR', ms: 42, fill: '1.5,18 1.5,11.9 8.5,12.8 15.5,11 22.5,12.8 29.5,11.9 36.5,13.7 43.5,12.8 50.5,13.7 50.5,18', line: '1.5,11.9 8.5,12.8 15.5,11 22.5,12.8 29.5,11.9 36.5,13.7 43.5,12.8 50.5,13.7', dotY: '13.7' },
  { id: 'r4', code: 'SCL', city: 'Santiago', cc: 'CL', ms: 77, fill: '1.5,18 1.5,5.2 8.5,7.9 15.5,4.3 22.5,7 29.5,5.2 36.5,6.1 43.5,8.8 50.5,6.1 50.5,18', line: '1.5,5.2 8.5,7.9 15.5,4.3 22.5,7 29.5,5.2 36.5,6.1 43.5,8.8 50.5,6.1', dotY: '6.1' },
  { id: 'r5', code: 'BUE', city: 'Buenos Aires', cc: 'AR', ms: 68, fill: '1.5,18 1.5,8.8 8.5,6.1 15.5,9.7 22.5,7 29.5,7.9 36.5,9.7 43.5,6.1 50.5,7.9 50.5,18', line: '1.5,8.8 8.5,6.1 15.5,9.7 22.5,7 29.5,7.9 36.5,9.7 43.5,6.1 50.5,7.9', dotY: '7.9' },
] as const;
const STATIC_WINNER_MS = 42;
const STATIC_BAD_MS = 208;

function countryNames(htmlLang: string): (code: string) => string {
  try {
    const names = new Intl.DisplayNames([htmlLang, 'en'], { type: 'region' });
    return (code) => names.of(code) ?? code;
  } catch {
    return (code) => code;
  }
}

function listJoin(htmlLang: string, items: string[]): string {
  try {
    return new Intl.ListFormat(htmlLang, { style: 'long', type: 'conjunction' }).format(items);
  } catch {
    return items.join(', ');
  }
}

export function Multiconexao() {
  const { t } = useTranslation(V1A_NAMESPACE);
  const htmlLang = useHtmlLang();
  const { openTrial } = useV1aTrial();
  const sectionRef = useRef<HTMLElement>(null);

  const strings = useMemo<McStrings>(() => {
    const text = (key: string, values: Record<string, unknown> = {}) => t(`mc.${key}`, values) as string;
    const html = (key: string, values: Record<string, unknown>) => t(`mc.${key}`, { ...values, interpolation: { escapeValue: false } }) as string;
    return {
      pcPlaceDefault: text('pcPlace'),
      gamePlace: (city) => text('gamePlace', { city }),
      desc: (routes, hub) => text('desc', { routes: listJoin(htmlLang, routes), hub }),
      deltaActive: text('deltaActive'),
      delta: (ms) => text('delta', { ms }),
      routeLabelWinner: (city, country, ms) => text('routeLabelWinner', { city, country, ms }),
      routeLabel: (city, country, ms, delta) => text('routeLabel', { city, country, ms, delta }),
      routeLabelMeasuring: (city, country) => text('routeLabelMeasuring', { city, country }),
      directLabel: (ms) => text('directLabel', { ms }),
      directLabelMeasuring: text('directLabelMeasuring'),
      proofSr: (bad, good, pct) => text('proofSr', { bad, good, pct }),
      statusChanged: (previous, previousMs, best, bestMs) => html('statusChanged', { previous, previousMs, best, bestMs }),
      statusMeasured: (best, ms) => html('statusMeasured', { best, ms }),
      statusLeading: (best, ms) => html('statusLeading', { best, ms }),
      statusPaused: (best, ms) => html('statusPaused', { best, ms }),
      statusFocusWinner: (city, ms) => html('statusFocusWinner', { city, ms }),
      statusFocusNext: (city, delta, best) => html('statusFocusNext', { city, delta, best }),
      statusFocusReserve: (city, delta, best) => html('statusFocusReserve', { city, delta, best }),
      statusMeasuring: text('statusMeasuring'),
      liveMeasuring: text('liveMeasuring'),
      liveMeasured: (city, ms, pct) => text('liveMeasured', { city, ms, pct }),
      liveMeasuredChanged: (city, ms, previous, pct) => text('liveMeasuredChanged', { city, ms, previous, pct }),
      livePinnedWinner: (city, ms) => text('livePinnedWinner', { city, ms }),
      livePinned: (city, ms, delta, best) => text('livePinned', { city, ms, delta, best }),
      liveCleared: text('liveCleared'),
      livePaused: text('livePaused'),
      liveResumed: text('liveResumed'),
      buttonIdle: text('retest'),
      buttonBusy: text('measuring'),
    };
  }, [t, htmlLang]);

  useEffect(() => {
    const root = sectionRef.current;
    if (!root) return undefined;
    let cancelled = false;
    let stop: (() => void) | null = null;
    loadGeoData(htmlLang).then((data) => {
      if (cancelled) return;
      stop = startMulticonexao(root, data, strings, { search: window.location.search, reduced: reducedMotionQuery() });
    });
    return () => {
      cancelled = true;
      stop?.();
    };
  }, [htmlLang, strings]);

  const handleCta = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    openTrial();
  };

  const country = countryNames(htmlLang);
  const staticDirectMs = STATIC_BAD_MS - STATIC_WINNER_MS;
  const staticPct = Math.round(((STATIC_BAD_MS - STATIC_WINNER_MS) / STATIC_BAD_MS) * 100);
  const bold = { b: <b /> };

  return (
    <div className={classNames(styles.root, 'v1-frame relative mx-auto mt-[clamp(2rem,1rem_+_3vw,3.25rem)] w-full max-w-[90rem] [container:v1f/inline-size]')} data-reveal>
      <section ref={sectionRef} className='mc' aria-labelledby='mc-title' data-mc>
        <div className='mc-card'>
          <header className='mc-head'>
            <span className='eyebrow'>{t('mc.eyebrow')}</span>
            <h2 className='mc-title' id='mc-title'>
              <span className='mc-title-a'>
                {t('mc.titleA')} <span className='mc-title-b'>{t('mc.titleB')}</span>
              </span>{' '}
              <em>{t('mc.titleWin')}</em>
            </h2>
            <p className='mc-lead'>
              <Trans t={t} i18nKey='mc.lead' components={bold} />
            </p>
            {/* role=list survives list-style:none in Safari/VoiceOver */}
            {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
            <ul className='mc-facts' role='list'>
              <li>
                <svg viewBox='0 0 24 24' aria-hidden='true'>
                  <path d='M14 3 5 13.5h6.5L10 21l9-10.5h-6.5z' />
                </svg>
                <span>
                  <Trans t={t} i18nKey='mc.factSwitch' components={bold} />
                </span>
              </li>
              <li>
                <svg viewBox='0 0 24 24' aria-hidden='true'>
                  <circle cx='12' cy='12' r='9' />
                  <path d='M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18' />
                </svg>
                <span>
                  <Trans t={t} i18nKey='mc.factServers' components={bold} />
                </span>
              </li>
            </ul>
          </header>
          <div className='mc-proof' role='group' aria-label={t('mc.proofLabel')} data-proof>
            <p className='sr-only' data-proof-sr>
              {strings.proofSr(STATIC_BAD_MS, STATIC_WINNER_MS, staticPct)}
            </p>
            <div className='mc-proof-rows' aria-hidden='true'>
              <div className='mc-proof-item mc-proof-item--bad'>
                <span className='mc-proof-line'>
                  <small>{t('mc.without')}</small>
                  <strong>
                    <span data-proof-bad>{STATIC_BAD_MS}</span>
                    <span className='u'>ms</span>
                  </strong>
                </span>
                <span className='mc-proof-bar'>
                  <span className='mc-proof-fill' />
                </span>
              </div>
              <div className='mc-proof-item mc-proof-item--good'>
                <span className='mc-proof-line'>
                  <small>{t('mc.with')}</small>
                  <span className='mc-proof-pct'>
                    −<span data-proof-pct>{staticPct}</span>%
                  </span>
                  <strong>
                    <span data-proof-good>{STATIC_WINNER_MS}</span>
                    <span className='u'>ms</span>
                  </strong>
                </span>
                <span className='mc-proof-bar'>
                  <span className='mc-proof-fill [--mc-fill:20.2%]' data-proof-fill />
                </span>
              </div>
            </div>
            <p className='mc-proof-note'>
              <Trans t={t} i18nKey='mc.proofNote' components={bold} />
            </p>
          </div>
          <p className='sr-only' id='mc-desc'>
            {t('mc.descStatic')}
          </p>
          <div className='mc-stage' data-stage role='group' aria-label={t('mc.stageLabel')} aria-describedby='mc-desc' dir='ltr'>
            <svg className='mc-defs' aria-hidden='true' focusable='false'>
              <defs>
                <linearGradient id='mc-spg' x1='0' y1='0' x2='0' y2='1'>
                  <stop className='g0' offset='0' />
                  <stop className='g1' offset='1' />
                </linearGradient>
              </defs>
            </svg>
            <svg className='mc-wires' data-wires aria-hidden='true' focusable='false' />
            <div className='mc-packets' data-packets aria-hidden='true' />
            <div className='mc-node mc-node--pc' data-node='pc'>
              <div className='mc-node-icon' aria-hidden='true'>
                <svg viewBox='0 0 40 40'>
                  <rect x='5' y='7' width='30' height='20' rx='3' />
                  <path d='M14 34h12M20 27v7' />
                  <circle cx='20' cy='17' r='5' />
                  <path d='M20 9v3.5M20 21.5V25M12 17h3.5M24.5 17H28' />
                  <circle className='f' cx='20' cy='17' r='1.4' />
                </svg>
              </div>
              <div className='mc-node-label'>
                <strong>{t('mc.pc')}</strong>
                <small data-pc-place>{t('mc.pcPlace')}</small>
              </div>
            </div>
            <div className='mc-routes-wrap'>
              <div className='mc-key'>
                <span className='mc-key-col' aria-hidden='true'>
                  <span className='x-long'>
                    <Trans t={t} i18nKey='mc.keyLong' components={bold} />
                  </span>
                  <span className='x-short'>
                    <b>{t('mc.keyShort')}</b>
                  </span>
                </span>
                <p className='mc-hint' data-hint aria-hidden='true'>
                  <svg className='x-touch' viewBox='0 0 24 24'>
                    <circle className='dot' cx='15' cy='15' r='2.4' />
                    <path d='M9.17 17.12A6.2 6.2 0 0 1 17.12 9.17M5.04 18.63A10.6 10.6 0 0 1 18.63 5.04' />
                  </svg>
                  <svg className='x-hover' viewBox='0 0 24 24'>
                    <path d='M5 3l14 8.2-6.4 1.6L9.4 19z' />
                  </svg>
                  <span className='x-touch'>{t('mc.hintTouch')}</span>
                  <span className='x-hover'>{t('mc.hintHover')}</span>
                </p>
                <button className='mc-clear' type='button' data-clear hidden title={t('mc.clearTitle')} aria-label={t('mc.clearLabel')}>
                  <svg viewBox='0 0 12 12' aria-hidden='true'>
                    <path d='M2.5 2.5l7 7M9.5 2.5l-7 7' />
                  </svg>
                  <span className='x-long'>{t('mc.clearLabel')}</span>
                  <span className='x-short'>{t('mc.clearShort')}</span>
                </button>
              </div>
              {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- role=list survives list-style:none in Safari/VoiceOver */}
              <ul className='mc-routes' role='list' data-routes aria-label={t('mc.routesLabel')}>
                {STATIC_ROUTES.map((route) => {
                  const isWinner = route.ms === STATIC_WINNER_MS;
                  const routeCountry = country(route.cc);
                  return (
                    <li key={route.id}>
                      <button className={classNames('mc-route', isWinner && 'is-winner')} type='button' data-route={route.id} aria-pressed='false' aria-label={isWinner ? strings.routeLabelWinner(route.city, routeCountry, route.ms) : strings.routeLabel(route.city, routeCountry, route.ms, route.ms - STATIC_WINNER_MS)}>
                        <span className='mc-route-cc' aria-hidden='true'>
                          {route.code}
                        </span>
                        <span className='mc-route-body'>
                          <span className='mc-route-city'>{route.city}</span>
                          <span className='mc-route-meta'>
                            <span className='mc-route-country'>{routeCountry}</span>
                            <span className='mc-route-tag'>{t('mc.tagFastest')}</span>
                            <span className='mc-route-pin'>
                              <svg viewBox='0 0 16 16' aria-hidden='true'>
                                <path d='M9.6 1.8 14.2 6.4l-2 .5-1.8 1.8.4 3.4-1.3 1.3-2.9-2.9-4.2 4.2m3-5.5L2.6 6.4l1.3-1.3 3.4.4 1.8-1.8z' />
                              </svg>
                              {t('mc.pinned')}
                            </span>
                          </span>
                        </span>
                        <svg className='mc-route-spark' viewBox='0 0 52 18' aria-hidden='true' focusable='false'>
                          <polygon className='sp-fill' points={route.fill} />
                          <polyline points={route.line} />
                          <circle className='sp-dot' r='1.75' cx='50.5' cy={route.dotY} />
                        </svg>
                        <span className='mc-route-lat'>
                          <span className='mc-route-ms'>
                            <span data-ms>{route.ms}</span>
                            <span className='u'>ms</span>
                          </span>
                          <span className='mc-route-delta' data-delta>
                            {isWinner ? strings.deltaActive : strings.delta(route.ms - STATIC_WINNER_MS)}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
              <p className='mc-direct-key' aria-hidden='true'>
                {t('mc.directKey')}
              </p>
              <div className='mc-direct' data-direct role='group' aria-label={strings.directLabel(staticDirectMs)}>
                <i aria-hidden='true' />
                <span>
                  <b>{t('mc.without')}</b>
                  <span className='x-d'>
                    <span className='x-sep' />
                    {t('mc.directSub')}
                  </span>
                </span>
                <strong>
                  +<span data-direct-ms>{staticDirectMs}</span>
                  <span className='u'>ms</span>
                </strong>
              </div>
            </div>
            <div className='mc-node mc-node--game' data-node='game'>
              <div className='mc-node-icon' aria-hidden='true'>
                <svg viewBox='0 0 40 40'>
                  <rect x='7' y='7' width='26' height='10' rx='2.5' />
                  <rect x='7' y='23' width='26' height='10' rx='2.5' />
                  <path d='M12 12h6M12 28h6' />
                  <circle className='f' cx='27' cy='12' r='1.6' />
                  <circle className='f' cx='27' cy='28' r='1.6' />
                </svg>
              </div>
              <div className='mc-node-label'>
                <strong>{t('mc.game')}</strong>
                <small data-game-place>{t('mc.gamePlaceStatic')}</small>
              </div>
            </div>
            <div className='mc-result' aria-hidden='true' data-result>
              <span className='mc-result-side mc-result-side--bad'>
                <small>{t('mc.without')}</small>
                <b>
                  <span data-result-bad>{STATIC_BAD_MS}</span>
                  <i>ms</i>
                </b>
              </span>
              <span className='mc-result-line'>
                <svg viewBox='0 0 12 12'>
                  <path d='M3.5 1.5L8 6l-4.5 4.5' />
                </svg>
              </span>
              <span className='mc-result-side mc-result-side--good'>
                <small>{t('mc.with')}</small>
                <span className='mc-result-fig'>
                  <span className='mc-proof-pct'>
                    −<span data-result-pct>{staticPct}</span>%
                  </span>
                  <b>
                    <span data-result-good>{STATIC_WINNER_MS}</span>
                    <i>ms</i>
                  </b>
                </span>
              </span>
            </div>
          </div>
          <footer className='mc-foot'>
            <div className='mc-foot-text'>
              <p className='mc-status' data-status>
                <i aria-hidden='true' />
                {/* the engine rewrites this line (with live figures inside), so React hands it over as HTML */}
                {/* eslint-disable-next-line react/no-danger -- our own translation string, city names escaped by the engine */}
                <span className='mc-status-body' data-status-text dangerouslySetInnerHTML={{ __html: t('mc.statusInitial', { city: 'São Paulo', ms: STATIC_WINNER_MS, interpolation: { escapeValue: false } }) as string }} />
              </p>
              <div className='mc-foot-slot'>
                <p className='mc-bridge' data-bridge>
                  {t('mc.bridge')}
                </p>
              </div>
            </div>
            <div className='mc-actions'>
              <button className='mc-pause' type='button' data-pause aria-label={t('mc.pauseLabel')} aria-pressed='false' title={t('mc.pauseTitle')}>
                <svg className='ic-pause' viewBox='0 0 24 24' aria-hidden='true'>
                  <path d='M8.5 5.5v13M15.5 5.5v13' />
                </svg>
                <svg className='ic-play' viewBox='0 0 24 24' aria-hidden='true'>
                  <path d='M8 5.2v13.6a.8.8 0 0 0 1.2.7l11-6.8a.8.8 0 0 0 0-1.4l-11-6.8a.8.8 0 0 0-1.2.7z' />
                </svg>
              </button>
              <button className='btn btn-secondary mc-btn' type='button' data-retest aria-busy='false'>
                <svg viewBox='0 0 24 24' aria-hidden='true'>
                  <path d='M20 11a8 8 0 1 0-2.3 5.7M20 4v7h-7' />
                </svg>
                <span className='mc-btn-lbl'>
                  <span data-lbl>{t('mc.retest')}</span>
                  <span className='mc-btn-ghost' aria-hidden='true'>
                    {t('mc.retest')}
                  </span>
                  <span className='mc-btn-ghost' aria-hidden='true'>
                    {t('mc.measuring')}
                  </span>
                </span>
              </button>
              <div className='mc-offer'>
                <a className='btn btn-primary mc-cta' data-cta href='#cadastro' onClick={handleCta}>
                  <span>{t('mc.cta')}</span>
                  <svg viewBox='0 0 24 24' aria-hidden='true'>
                    <path d='M5 12h13M13 6l6 6-6 6' />
                  </svg>
                </a>
                <p className='mc-offer-note'>
                  <Trans t={t} i18nKey='mc.offerNote' components={bold} />
                </p>
              </div>
            </div>
            <p className='sr-only' role='status' aria-live='polite' data-live />
          </footer>
        </div>
      </section>
    </div>
  );
}
